import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ProjectData } from '../../models';

export interface CopyChapterSelection {
	chapterSlug: string | null;
}

interface ChapterOption {
	label: string;
	chapterSlug: string | null;
	sceneCount: number;
	imageCount: number;
}

@Component({
	selector: 'app-copy-chapter-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, FormsModule, ButtonDirective, InputText],
	templateUrl: './copy-chapter-dialog.html',
	styleUrl: './copy-chapter-dialog.scss',
})
export class CopyChapterDialogComponent {
	visible = model(false);
	project = input<ProjectData | undefined>(undefined);
	sourceLabel = input<string>('');
	header = input<string>('Copy to chapter');

	targetSelected = output<CopyChapterSelection>();

	protected searchQuery = signal('');
	protected selected = signal<CopyChapterSelection | null>(null);
	private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	protected options = computed<ChapterOption[]>(() => {
		const project = this.project();
		const out: ChapterOption[] = (project?.chapters ?? []).map((chapter) => ({
			label: chapter.name?.trim() || chapter.slug,
			chapterSlug: chapter.slug,
			sceneCount: (chapter.scenes ?? []).length,
			imageCount: (chapter.images ?? []).length,
		}));
		// Always offered, even with no project-level entries yet — it's a valid destination.
		out.push({
			label: 'Project level (no chapter)',
			chapterSlug: null,
			sceneCount: (project?.scenes ?? []).length,
			imageCount: (project?.images ?? []).length,
		});
		return out;
	});

	protected filteredOptions = computed<ChapterOption[]>(() => {
		const query = this.searchQuery().toLowerCase().trim();
		if (!query) return this.options();
		return this.options().filter(
			(o) => o.label.toLowerCase().includes(query) || (o.chapterSlug ?? '').toLowerCase().includes(query),
		);
	});

	protected isSelected(chapterSlug: string | null): boolean {
		const sel = this.selected();
		return !!sel && sel.chapterSlug === chapterSlug;
	}

	protected selectTarget(chapterSlug: string | null): void {
		this.selected.set({ chapterSlug });
	}

	protected confirm(): void {
		const sel = this.selected();
		if (!sel) return;
		this.targetSelected.emit(sel);
		this.visible.set(false);
	}

	protected cancel(): void {
		this.visible.set(false);
	}

	protected onHide(): void {
		this.searchQuery.set('');
		this.selected.set(null);
	}

	protected onShow(): void {
		this.searchInput()?.nativeElement.focus();
	}
}
